// Lean print fix-ups for the embed article HTML.
//
// (MJML is the email-markup framework pony compiles campaign bodies with; its
// output uses `.mj-column-*` classes for responsive multi-column layouts.)
//
// Most print-correctness is handled declaratively by the injected stylesheet
// (see buildPrintStyles): forced page-breaks (`* { break: auto !important }`),
// html/body 100vh centering (`display:block; min-height:0 !important`), empty
// spacer cells (`tr/td/th:empty`), and the common MJML column widths
// (`mj-column-per-{22,50,78,100}` + their `@media print` flex rules). We trust
// the browser's print engine and that CSS for the rest, rather than mutating
// the DOM.
//
// NOTE: `@media only screen` carries MJML's responsive column widths, which do
// NOT apply in print — so without re-asserting them, multi-column rows collapse
// and stack. The static set above covers the common splits; this pass covers
// the rest (notably 3-column `mj-column-per-33-333333`).
//
// The one thing CSS can't express is MJML column classes whose width isn't in
// that static set. MJML names a column class after its width with the decimal
// point turned into a dash, so a 3-column block (no explicit width → 100/3) is
// `mj-column-per-33-333333`, NOT `mj-column-per-33`; pixel columns
// (`mj-column-px-200`, file-attachment blocks) are another case. MJML's
// `mj-outlook-group-fix { width:100% }` would otherwise collapse them to full
// width and stack them. We discover the column tokens actually present and emit
// the matching width rules (converting the dash back to a decimal point).
export function sanitizePdfStyles(doc: Document): void {
  const covered = new Set(['22', '50', '78', '100']);
  const missingPct = new Set<string>();
  const missingPx = new Set<string>();

  doc.querySelectorAll('[class]').forEach((el) => {
    const cls = (el.className || '').toString();
    // [\d-]+ captures decimal widths too: `mj-column-per-33-333333` → 33-333333.
    const perMatches = cls.match(/mj-column-per-([\d-]+)/g);
    if (perMatches)
      perMatches.forEach((c) => {
        const token = c.replace('mj-column-per-', '');
        if (!covered.has(token)) missingPct.add(token);
      });
    const pxMatches = cls.match(/mj-column-px-(\d+)/g);
    if (pxMatches)
      pxMatches.forEach((c) => {
        missingPx.add(c.replace('mj-column-px-', ''));
      });
  });

  const rules: string[] = [];
  missingPct.forEach((token) => {
    // The class token uses a dash for the decimal point; CSS width uses a dot.
    const widthValue = token.replace(/-/g, '.');
    if (!/^\d+(\.\d+)?$/.test(widthValue)) return; // skip malformed tokens
    rules.push(
      `.mj-column-per-${token} { width:${widthValue}% !important; max-width:${widthValue}% !important; }` +
        `@media print { .mj-column-per-${token} { display:inline-block !important; flex:0 0 ${widthValue}% !important; } }`
    );
  });
  missingPx.forEach((px) => {
    rules.push(
      `.mj-column-px-${px} { width:${px}px !important; max-width:${px}px !important; }` +
        `@media print { .mj-column-px-${px} { display:inline-block !important; vertical-align:top !important; } }`
    );
  });

  if (rules.length > 0) {
    const style = doc.createElement('style');
    style.setAttribute('data-pdf-injected', '1');
    style.textContent = rules.join('\n');
    doc.head.appendChild(style);
  }
}
